/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Eye, Edit, Building2, ChevronLeft, ChevronRight, RefreshCw, MoreVertical, Wand2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrganizations, useGenerateTestData } from '@/hooks/api/useOrganizations';
import { OrganizationListItem, OrganizationStatus, FilterableOrganizationStatus, SubscriptionTier } from '@/types/superadmin.types';
import { formatCurrency } from '@/lib/utils';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import { SearchInput } from '@/components/ui';

// Status Badge Component
const StatusBadge: React.FC<{ status: OrganizationStatus; t: (key: string) => string }> = ({ status, t }) => {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-emerald-500/15 text-emerald-500',
    TRIAL: 'bg-blue-500/15 text-blue-500',
    SUSPENDED: 'bg-red-500/15 text-red-500',
    CANCELLED: 'bg-gray-500/15 text-gray-500',
    PENDING_APPROVAL: 'bg-amber-500/15 text-amber-500',
  };

  const labelKeys: Record<OrganizationStatus, string> = {
    ACTIVE: 'organizations.status.active',
    TRIAL: 'organizations.status.trial',
    SUSPENDED: 'organizations.status.suspended',
    CANCELLED: 'organizations.status.cancelled',
    PENDING_APPROVAL: 'organizations.status.pending',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
      {t(labelKeys[status])}
    </span>
  );
};

// Plan Badge Component
const PlanBadge: React.FC<{ tier: OrganizationListItem['subscriptionTier'] }> = ({ tier }) => {
  const styles = {
    STARTER: 'bg-gray-500/15 text-gray-400',
    PROFESSIONAL: 'bg-purple-500/15 text-purple-500',
    ENTERPRISE: 'bg-red-500/15 text-red-500',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${styles[tier]}`}>
      {tier}
    </span>
  );
};

// Avatar Component
const OrgAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  const gradients = [
    'from-red-600 to-red-800',
    'from-emerald-500 to-cyan-500',
    'from-amber-500 to-orange-500',
    'from-purple-500 to-pink-500',
    'from-blue-500 to-indigo-500',
  ];
  const gradient = gradients[name.charCodeAt(0) % gradients.length];

  return (
    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-sm font-semibold`}>
      {initials}
    </div>
  );
};

// Tab Component
const Tab: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active
        ? 'bg-primary text-white'
        : 'text-muted hover:text-foreground hover:bg-hover'
    }`}
  >
    {label} ({count})
  </button>
);

// Action Menu Component
const ActionMenu: React.FC<{
  orgId: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onView: () => void;
  onEdit: () => void;
  t: (key: string) => string;
  openUpward?: boolean;
}> = ({ isOpen, onToggle, onClose, onView, onEdit, t, openUpward = false }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {isOpen && (
        <div className={`absolute right-0 w-32 bg-surface border border-border rounded-lg shadow-lg z-[100] py-1 ${
          openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}>
          <button
            onClick={(e) => { e.stopPropagation(); onView(); onClose(); }}
            className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover flex items-center gap-2"
          >
            <Eye className="w-4 h-4" /> View
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); onClose(); }}
            className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover flex items-center gap-2"
          >
            <Edit className="w-4 h-4" /> {t('common.edit')}
          </button>
        </div>
      )}
    </div>
  );
};

// Main Organizations Page
export default function OrganizationsPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateParams, setGenerateParams] = useState({
    organizationCount: 5,
    partnerCount: 2,
    linkToPartners: true
  });

  const generateTestData = useGenerateTestData();

  // Read initial status from URL params
  const urlStatus = searchParams.get('status');
  const validStatuses: (FilterableOrganizationStatus | 'ALL')[] = ['ALL', 'ACTIVE', 'TRIAL', 'EXPIRED_TRIAL', 'SUSPENDED', 'CANCELLED', 'PENDING_APPROVAL'];
  const initialStatus = urlStatus && validStatuses.includes(urlStatus as FilterableOrganizationStatus) ? urlStatus as FilterableOrganizationStatus : 'ALL';
  const [statusFilter, setStatusFilter] = useState<FilterableOrganizationStatus | 'ALL'>(initialStatus);

  // Read initial plan from URL params
  const urlPlan = searchParams.get('plan');
  const validPlans: (SubscriptionTier | 'ALL')[] = ['ALL', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];
  const initialPlan = urlPlan && validPlans.includes(urlPlan as SubscriptionTier) ? urlPlan as SubscriptionTier : 'ALL';
  const [planFilter, setPlanFilter] = useState<SubscriptionTier | 'ALL'>(initialPlan);

  // Sorting state
  const { sortConfig, handleSort, getSortParam } = useSort('name', 'asc');

  // Update URL when filters change
  useEffect(() => {
    if (statusFilter === 'ALL') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', statusFilter);
    }
    if (planFilter === 'ALL') {
      searchParams.delete('plan');
    } else {
      searchParams.set('plan', planFilter);
    }
    setSearchParams(searchParams, { replace: true });
  }, [statusFilter, planFilter, searchParams, setSearchParams]);

  // Use the organizations hook with filters
  const { data, isLoading, isFetching, refetch } = useOrganizations({
    page: currentPage,
    size: 20,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    subscriptionTier: planFilter !== 'ALL' ? planFilter : undefined,
    search: searchQuery || undefined,
    sort: getSortParam(),
  });

  const organizations = data?.content || [];
  const totalPages = data?.page?.totalPages || 1;
  const totalElements = data?.page?.totalElements || 0;

  // Calculate status counts from current data
  const statusCounts = useMemo(() => {
    // Note: In a real app, these would come from the API
    // For now, we'll use the filtered total
    return {
      ALL: totalElements,
      ACTIVE: organizations.filter(o => o.status === 'ACTIVE').length,
      TRIAL: organizations.filter(o => o.status === 'TRIAL').length,
      EXPIRED_TRIAL: 0, // This count comes from the server when filtering
      SUSPENDED: organizations.filter(o => o.status === 'SUSPENDED').length,
    };
  }, [organizations, totalElements]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="orgs-header flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('organizations.title')}</h1>
            <p className="text-sm text-muted">{t('organizations.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowGenerateModal(true)}
              disabled={generateTestData.isPending}
              className="flex items-center gap-2 px-3 py-2 border border-border text-foreground rounded-lg text-sm font-medium hover:bg-hover disabled:opacity-50"
              title={t('organizations.generateTestData', 'Generate Test Data')}
            >
              <Wand2 className={`w-4 h-4 ${generateTestData.isPending ? 'animate-spin' : ''}`} />
              <span className="orgs-generate-text">{t('organizations.generateTestData', 'Generate')}</span>
            </button>
            <Link
              to="/superadmin/organizations/new"
              className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" /> <span className="orgs-new-text">{t('organizations.newOrg')}</span>
            </Link>
          </div>
        </div>
        <div className="orgs-filters flex flex-wrap items-center gap-2">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('organizations.search')}
            className="flex-1 min-w-[150px] max-w-xs"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FilterableOrganizationStatus | 'ALL')}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none"
          >
            <option value="ALL">{t('organizations.filter.allStatus')}</option>
            <option value="ACTIVE">{t('organizations.status.active')}</option>
            <option value="TRIAL">{t('organizations.status.trial')}</option>
            <option value="EXPIRED_TRIAL">{t('organizations.status.expiredTrial')}</option>
            <option value="SUSPENDED">{t('organizations.status.suspended')}</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value as SubscriptionTier | 'ALL')}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none"
          >
            <option value="ALL">{t('organizations.filter.allPlans')}</option>
            <option value="STARTER">{t('organizations.plans.starter')}</option>
            <option value="PROFESSIONAL">{t('organizations.plans.professional')}</option>
            <option value="ENTERPRISE">{t('organizations.plans.enterprise')}</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="orgs-tabs flex gap-2 p-1 bg-background rounded-lg w-fit">
        <Tab label={t('organizations.tabs.all')} count={statusCounts.ALL} active={statusFilter === 'ALL'} onClick={() => setStatusFilter('ALL')} />
        <Tab label={t('organizations.tabs.active')} count={statusCounts.ACTIVE} active={statusFilter === 'ACTIVE'} onClick={() => setStatusFilter('ACTIVE')} />
        <Tab label={t('organizations.tabs.trial')} count={statusCounts.TRIAL} active={statusFilter === 'TRIAL'} onClick={() => setStatusFilter('TRIAL')} />
        <Tab label={t('organizations.tabs.expiredTrial')} count={statusCounts.EXPIRED_TRIAL} active={statusFilter === 'EXPIRED_TRIAL'} onClick={() => setStatusFilter('EXPIRED_TRIAL')} />
        <Tab label={t('organizations.tabs.suspended')} count={statusCounts.SUSPENDED} active={statusFilter === 'SUSPENDED'} onClick={() => setStatusFilter('SUSPENDED')} />
      </div>

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-visible">
        {/* Table Header */}
        <div className="orgs-table-header grid grid-cols-[2fr_1fr_1fr_80px_90px_90px_100px_60px] gap-3 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider col-org">
            <SortableHeader columnKey="name" label={t('organizations.table.organization')} sortConfig={sortConfig} onSort={handleSort} />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider col-plan">
            <SortableHeader columnKey="subscriptionTier" label={t('organizations.table.plan')} sortConfig={sortConfig} onSort={handleSort} />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider col-status">
            <SortableHeader columnKey="status" label={t('organizations.table.status')} sortConfig={sortConfig} onSort={handleSort} />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider col-billing">
            <SortableHeader columnKey="nextBillingDate" label={t('organizations.table.nextBilling', 'Next Billing')} sortConfig={sortConfig} onSort={handleSort} />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider col-subdate">
            <SortableHeader columnKey="subscriptionStartDate" label={t('organizations.table.subDate', 'Sub Date')} sortConfig={sortConfig} onSort={handleSort} />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider col-mrr-usd">
            <SortableHeader columnKey="monthlyPrice" label={t('organizations.table.mrrUsd', 'MRR (USD)')} sortConfig={sortConfig} onSort={handleSort} align="right" />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider col-mrr-local">
            <SortableHeader columnKey="monthlyPriceLocal" label={t('organizations.table.mrrLocal', 'MRR (Local)')} sortConfig={sortConfig} onSort={handleSort} align="right" />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-actions">{t('organizations.table.actions')}</div>
        </div>

        {/* Table Body */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : organizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <Building2 className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('organizations.empty')}</p>
          </div>
        ) : (
          organizations.map((org, index) => {
            // Calculate days until next billing
            const getDaysUntilBilling = (dateStr: string | undefined): string => {
              if (!dateStr) return '-';
              const billingDate = new Date(dateStr);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const days = Math.ceil((billingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              if (days < 0) return t('organizations.table.overdue', 'Overdue');
              if (days === 0) return t('organizations.table.today', 'Today');
              return `${days}d`;
            };

            // Format subscription date
            const formatSubDate = (dateStr: string | undefined): string => {
              if (!dateStr) return '-';
              return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
            };

            return (
              <div
                key={org.id}
                onClick={() => navigate(`/superadmin/organizations/${org.id}`)}
                className="orgs-table-row grid grid-cols-[2fr_1fr_1fr_80px_90px_90px_100px_60px] gap-3 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover cursor-pointer transition-colors"
              >
                <div className="col-org flex items-center gap-3">
                  <OrgAvatar name={org.name} />
                  <div>
                    <div className="font-medium text-foreground">{org.name}</div>
                    <div className="text-xs text-muted">{org.primaryContactEmail}</div>
                  </div>
                </div>
                <div className="col-plan"><PlanBadge tier={org.subscriptionTier} /></div>
                <div className="col-status"><StatusBadge status={org.status} t={t} /></div>
                <div className="text-sm text-foreground col-billing">{getDaysUntilBilling(org.nextBillingDate)}</div>
                <div className="text-sm text-muted col-subdate">{formatSubDate(org.subscriptionStartDate)}</div>
                <div className="font-mono text-sm text-foreground col-mrr-usd">
                  {(org.monthlyPriceUsd ?? org.monthlyPrice) > 0 ? formatCurrency(org.monthlyPriceUsd ?? org.monthlyPrice, 'USD') : '-'}
                </div>
                <div className="font-mono text-sm text-foreground col-mrr-local">
                  {org.monthlyPrice > 0 && org.currency !== 'USD' ? formatCurrency(org.monthlyPrice, org.currency) : '-'}
                </div>
                <div className="col-actions flex justify-end">
                  <ActionMenu
                    orgId={org.id}
                    isOpen={openMenuId === org.id}
                    onToggle={() => setOpenMenuId(openMenuId === org.id ? null : org.id)}
                    onClose={() => setOpenMenuId(null)}
                    onView={() => navigate(`/superadmin/organizations/${org.id}`)}
                    onEdit={() => navigate(`/superadmin/organizations/${org.id}/edit`)}
                    t={t}
                    openUpward={index >= organizations.length - 2}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">
          {totalElements > 0 ? t('organizations.pagination.showing', {
            from: currentPage * 20 + 1,
            to: Math.min((currentPage + 1) * 20, totalElements),
            total: totalElements
          }) : t('organizations.pagination.noResults')}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted">
            {t('organizations.pagination.page', { current: currentPage + 1, total: Math.max(1, totalPages) })}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Generate Test Data Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Wand2 className="w-5 h-5" />
              {t('organizations.generateTestDataTitle', 'Generate Test Data')}
            </h2>
            <p className="text-sm text-muted mb-4">
              {t('organizations.generateTestDataDesc', 'Create random organizations with Guinea-context data for testing.')}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('organizations.orgCount', 'Organizations')}
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={generateParams.organizationCount}
                  onChange={(e) => setGenerateParams(prev => ({
                    ...prev,
                    organizationCount: Math.min(50, Math.max(1, parseInt(e.target.value) || 1))
                  }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('organizations.partnerCount', 'Partners')}
                </label>
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={generateParams.partnerCount}
                  onChange={(e) => setGenerateParams(prev => ({
                    ...prev,
                    partnerCount: Math.min(20, Math.max(0, parseInt(e.target.value) || 0))
                  }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="linkToPartners"
                  checked={generateParams.linkToPartners}
                  onChange={(e) => setGenerateParams(prev => ({
                    ...prev,
                    linkToPartners: e.target.checked
                  }))}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary/50"
                />
                <label htmlFor="linkToPartners" className="text-sm text-foreground">
                  {t('organizations.linkToPartners', 'Link organizations to partners randomly')}
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-hover"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  generateTestData.mutate(generateParams, {
                    onSuccess: (data) => {
                      setShowGenerateModal(false);
                      alert(data.message);
                    },
                    onError: (error) => {
                      alert(error.message || 'Failed to generate test data');
                    }
                  });
                }}
                disabled={generateTestData.isPending}
                className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {generateTestData.isPending && <RefreshCw className="w-4 h-4 animate-spin" />}
                {t('organizations.generate', 'Generate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .orgs-table-header,
          .orgs-table-row {
            display: flex !important;
            align-items: center;
            gap: 8px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          .col-plan,
          .col-users,
          .col-mrr-usd,
          .col-mrr-local,
          .col-billing,
          .col-subdate {
            display: none !important;
          }
          .col-org {
            flex: 1;
            min-width: 0;
          }
          .col-status {
            flex-shrink: 0;
            text-align: center;
          }
          .col-actions {
            flex-shrink: 0;
            display: flex;
            justify-content: flex-end;
          }
          .orgs-tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .orgs-new-text,
          .orgs-generate-text {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
